import { useTransaction } from '../hooks/useTransaction';
import { useAccount } from '../state/wallet.state';

import { useAtom, useSetAtom } from 'jotai';
import { Network } from 'near-safe';
import { useEffect, useMemo, useRef, useState } from 'react';
import { serializeSignature } from 'viem';
import { EvmTxCardProps } from '../components/chat/transactions/evm/types';
import { createEvmTransactionStatusMessage } from '../lib/transaction-messages';
import {
  TransactionState,
  TransactionStatus,
  activeTransactionIdsAtom,
  createTxId,
  initTransaction,
  transactionAtomFamily,
} from '../store/evmTransactionStore';
import {
  checkTransactionStatus,
  ensureValidHash,
  getRpcUrlForChain,
} from '../utils/evm';

// Ensure evmData is required for the useEVMTransaction hook
export type UseEVMTransactionParams = {
  evmData: NonNullable<EvmTxCardProps['evmData']> | undefined;
  append: EvmTxCardProps['append'];
  messageId: EvmTxCardProps['messageId'];
};

export const useEVMTransaction = ({
  evmData,
  append,
  messageId,
}: UseEVMTransactionParams) => {
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { evmAddress, evmWallet } = useAccount();
  const initialWalletHash = useRef<string | undefined>(evmWallet?.hash);
  const setActiveTransactionIds = useSetAtom(activeTransactionIdsAtom);
  const messageAppended = useRef(false);

  if (!evmData) {
    return {
      isLoading: false,
      errorMsg: '',
      setErrorMsg: () => {},
      transaction: null,
      handleSmartAction: async () => {},
      network: null,
      evmAddress: '',
      setTransaction: () => {},
    };
  }

  // Create a unique transaction ID for this card
  const txId = useMemo(() => {
    return createTxId(evmData);
  }, [evmData]);

  // Create and use the transaction atom for this specific transaction
  const [transaction, setTransaction] = useAtom(transactionAtomFamily(txId));

  useEffect(() => {
    return () => {
      if (txId) {
        setActiveTransactionIds((prev: string[]) =>
          prev.filter((id: string) => id !== txId),
        );
      }
    };
  }, [txId, setActiveTransactionIds]);

  useEffect(() => {
    if (!evmWallet) return;
    if (evmWallet.chainId !== evmData.chainId) {
      evmWallet.switchChain({ chainId: evmData.chainId });
    }
  }, [evmWallet, evmData.chainId]);

  const appendedSignatures = useRef<Set<string>>(new Set());
  const isSignatureMethod = (method: string) => {
    return [
      'eth_signTypedData',
      'eth_signTypedData_v4',
      'personal_sign',
      'eth_sign',
    ].includes(method);
  };

  const isWalletRejectionError = (error: Error) => {
    const errorMessage = error.message.toLowerCase();
    // Most common wallet rejection patterns
    return (
      errorMessage.includes('user rejected') ||
      errorMessage.includes('user denied') ||
      errorMessage.includes('user cancelled')
    );
  };

  const network = Network.fromChainId(evmData.chainId);
  const { handleTxn } = useTransaction({ evmWallet: evmWallet });

  const handleSmartAction = async () => {
    // Initialize transaction state
    if (!txId) return;

    setIsLoading(true);

    // Failsafe: ensure loading state is cleared after 30 seconds max
    const loadingTimeout = setTimeout(() => {
      console.warn(
        'Transaction loading timeout reached, clearing loading state',
      );
      setIsLoading(false);
    }, 30000);

    // Store initial wallet hash
    initialWalletHash.current = evmWallet?.hash;

    // Initialize transaction in store with fresh state
    const { initialState } = initTransaction(txId, messageId);
    setTransaction(initialState);

    // Clear any previous error messages and signature caches for fresh attempt
    setErrorMsg('');
    appendedSignatures.current.clear();
    messageAppended.current = false;

    // Add to active transactions list
    setActiveTransactionIds((prev: string[]) => [...prev, txId]);

    try {
      const adaptedEvmData = {
        ...evmData,
        metaTransactions: [],
      };

      // Execute transaction
      const result = await handleTxn({ evmData: adaptedEvmData });

      // For signature methods, let the polling effect handle the signature processing
      if (isSignatureMethod(evmData.method)) {
        // Keep loading state active for signature methods - polling will handle completion
        return;
      }

      // Check if we have transaction hashes
      const evmHashes = (result as any).evmHashes;

      if (evmHashes && Array.isArray(evmHashes) && evmHashes.length > 0) {
        const newTxHash = evmHashes[0];

        // If it's a placeholder hash (starts with 'pending-'), treat it as pending
        const isPending =
          typeof newTxHash === 'string' && newTxHash.startsWith('pending-');

        if (!isPending) {
          // Check if the transaction was successful
          const { success, errorMessage } = await checkTransactionStatus(
            newTxHash,
            evmData.chainId,
          );
          const txStatus = success ? 'success' : 'fail';

          // Update transaction state with real hash
          setTransaction((prev: TransactionState | null) => ({
            ...(prev || initialState),
            txHash: newTxHash,
            status: success ? 'success' : ('error' as TransactionStatus),
            error: errorMessage,
          }));

          if (!messageAppended.current) {
            messageAppended.current = true;
            await append(
              createEvmTransactionStatusMessage(
                evmData,
                txStatus,
                newTxHash,
                messageId,
                errorMessage,
              ),
            );
          }
        } else {
          // For placeholder hash, just keep loading state active

          // Set a temporary hash that will be updated by polling
          setTransaction((prev: TransactionState | null) => ({
            ...(prev || initialState),
            txHash: undefined, // Don't set placeholder hash to UI
            status: 'pending' as TransactionStatus,
          }));

          // Continue in loading state for polling to work
          // Don't append message yet
        }
      }
      // Fallback to wallet hash if result doesn't contain hash
      else if (
        evmWallet?.hash &&
        evmWallet.hash !== initialWalletHash.current
      ) {
        const newTxHash = ensureValidHash(evmWallet.hash);
        // Check if the transaction was successful
        const { success, errorMessage } = await checkTransactionStatus(
          newTxHash,
          evmData.chainId,
        );
        const txStatus = success ? 'success' : 'fail';

        setTransaction((prev: TransactionState | null) => ({
          ...(prev || initialState),
          txHash: newTxHash,
          status: success ? 'success' : ('error' as TransactionStatus),
          error: errorMessage,
        }));

        if (!messageAppended.current) {
          messageAppended.current = true;
          await append(
            createEvmTransactionStatusMessage(
              evmData,
              txStatus,
              newTxHash,
              messageId,
              errorMessage,
            ),
          );
        }
      }

      // Determine if we should stop loading based on transaction state
      const hasRealHash =
        evmHashes &&
        evmHashes.length > 0 &&
        !evmHashes[0].startsWith('pending-');
      const hasTransactionHash = !!transaction?.txHash;
      const hasPlaceholderHash =
        evmHashes &&
        evmHashes.length > 0 &&
        evmHashes[0].startsWith('pending-');

      // Stop loading if:
      // 1. We have a real transaction hash
      // 2. We have no hashes at all (transaction failed/cancelled)
      // Keep loading if we have a placeholder hash (polling will handle it)
      if (
        hasRealHash ||
        hasTransactionHash ||
        !evmHashes ||
        evmHashes.length === 0
      ) {
        clearTimeout(loadingTimeout);
        setIsLoading(false);
      }
      // If we only have a placeholder hash, keep loading for polling
      else if (hasPlaceholderHash) {
        // Loading state remains true for polling to work
        console.log(
          'Transaction submitted with placeholder hash, waiting for confirmation...',
        );
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : `Unknown error: ${JSON.stringify(error)}`;

      console.error(`Transaction ${txId} error:`, errorMessage);

      // Check if this is a user rejection (wallet cancellation)
      const isUserRejection =
        error instanceof Error && isWalletRejectionError(error);

      // Update transaction state with error
      setTransaction((prev: TransactionState | null) => ({
        ...(prev || initialState),
        status: isUserRejection
          ? ('cancelled' as TransactionStatus)
          : ('error' as TransactionStatus),
        error: isUserRejection ? 'Transaction cancelled by user' : errorMessage,
      }));

      clearTimeout(loadingTimeout);
      setIsLoading(false);
      setErrorMsg(
        isUserRejection ? 'Transaction cancelled by user' : errorMessage,
      );

      // Only append message for actual errors, not user rejections
      if (!messageAppended.current && !isUserRejection) {
        messageAppended.current = true;
        await append(
          createEvmTransactionStatusMessage(
            evmData,
            'fail',
            undefined,
            messageId,
            errorMessage,
          ),
        );
      }
    }
  };

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    let retryCount = 0;
    const MAX_RETRIES = 30; // 15 seconds total (30 * 500ms)

    if (
      isLoading &&
      (!transaction?.txHash || transaction?.status === 'pending')
    ) {
      interval = setInterval(() => {
        retryCount++;
        const currentHash = evmWallet?.hash;

        // Handle signature methods
        if (isSignatureMethod(evmData.method) && evmWallet?.signature) {
          clearInterval(interval!);

          const signature = serializeSignature(evmWallet.signature);

          // Only process if we haven't seen this signature before
          if (!appendedSignatures.current.has(signature)) {
            appendedSignatures.current.add(signature);

            // Update transaction state for signature methods
            setTransaction((prev: TransactionState | null) => ({
              ...(prev || {
                messageId,
                status: 'pending' as TransactionStatus,
                timestamp: Date.now(),
              }),
              status: 'success' as TransactionStatus,
              signature,
            }));

            // Append signature message to chat
            if (!messageAppended.current) {
              messageAppended.current = true;
              (async () => {
                await append({
                  role: 'system',
                  content: JSON.stringify({
                    text: 'user signed message',
                    signature,
                  }),
                });
              })();
            }
            // Clear wallet signature and stop loading
            evmWallet.signature = undefined;
            setIsLoading(false);
          }
        }
        // Handle transaction methods
        if (currentHash && currentHash !== initialWalletHash.current) {
          clearInterval(interval!);

          // Check transaction status before updating the UI
          (async () => {
            // Check if the transaction was successful
            const { success, errorMessage } = await checkTransactionStatus(
              ensureValidHash(currentHash),
              evmData.chainId,
            );
            const txStatus = success ? 'success' : 'fail';

            // Update transaction with hash and status
            setTransaction((prev: TransactionState | null) =>
              prev
                ? {
                    ...prev,
                    txHash: currentHash,
                    status: success
                      ? 'success'
                      : ('error' as TransactionStatus),
                    error: errorMessage,
                  }
                : null,
            );

            setIsLoading(false);

            // Append message if not already appended
            if (!messageAppended.current) {
              messageAppended.current = true;
              await append(
                createEvmTransactionStatusMessage(
                  evmData,
                  txStatus,
                  currentHash,
                  messageId,
                  errorMessage,
                ),
              );
            }
          })();
        }

        // Give up after maximum retries
        if (retryCount >= MAX_RETRIES) {
          clearInterval(interval!);

          if (isLoading) {
            setIsLoading(false);

            // For signature methods, if we don't have a signature after timeout, user likely declined
            if (isSignatureMethod(evmData.method) && !evmWallet?.signature) {
              // Update transaction state to cancelled
              setTransaction((prev: TransactionState | null) => ({
                ...(prev || { messageId, timestamp: Date.now() }),
                status: 'cancelled' as TransactionStatus,
                error: 'Transaction cancelled by user',
              }));

              setErrorMsg('Transaction cancelled by user');
              return; // Don't append fallback message
            }

            // If we still don't have a hash but the transaction was started,
            // set a fallback UI state
            if (!messageAppended.current) {
              messageAppended.current = true;
              (async () => {
                await append(
                  createEvmTransactionStatusMessage(
                    evmData,
                    'pending',
                    undefined,
                    messageId,
                    'Transaction submitted. Check your wallet history for confirmation.',
                  ),
                );
              })();
            }
          }
        }
      }, 500);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isLoading, transaction, evmWallet, evmData, messageId, setTransaction]);

  return {
    isLoading,
    setIsLoading,
    setErrorMsg,
    errorMsg,
    transaction,
    handleSmartAction,
    handleTxn,
    checkTransactionStatus,
    getRpcUrlForChain,
    network,
    evmData,
    evmAddress,
    setTransaction,
  };
};
